/**
 * Comment System Module
 * Handles all comment functionality for university pages
 */

export const commentSystem = {
    // Comment system state
    pendingCommentData: null,
    isLoadingComments: false,

    // Initialize comment system
    // DEPRECATED: Comments are now handled by Livewire component
    // This method is kept for backward compatibility but does nothing
    initializeCommentSystem() {
        // Comments are now handled by Livewire - no JavaScript initialization needed
        return;
    },

    // Setup comment form
    setupCommentForm() {
        const commentForm = document.getElementById('comment-form');
        if (!commentForm) return;

        commentForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.submitComment();
        });
    },

    // Setup comment interactions (like, dislike, reply, report)
    setupCommentInteractions() {
        // Use event delegation to handle dynamic comment elements
        document.addEventListener('click', (e) => {
            // Handle comment like button
            if (e.target.closest('.comment-like-btn')) {
                e.preventDefault();
                const likeBtn = e.target.closest('.comment-like-btn');
                this.handleCommentLike(likeBtn);
            }

            // Handle comment dislike button
            if (e.target.closest('.comment-dislike-btn')) {
                e.preventDefault();
                const dislikeBtn = e.target.closest('.comment-dislike-btn');
                this.handleCommentDislike(dislikeBtn);
            }

            // Handle comment reply button
            if (e.target.closest('.comment-reply-btn')) {
                e.preventDefault();
                const replyBtn = e.target.closest('.comment-reply-btn');
                this.handleCommentReply(replyBtn);
            }

            // Handle comment report button
            if (e.target.closest('.comment-report-btn')) {
                e.preventDefault();
                const reportBtn = e.target.closest('.comment-report-btn');
                this.handleCommentReport(reportBtn);
            }
        });

        // Handle reply form submission
        document.addEventListener('submit', (e) => {
            if (e.target.classList.contains('reply-form')) {
                e.preventDefault();
                this.submitReply(e.target);
            }
        });
    },

    // Setup load more comments
    setupLoadMoreComments() {
        const loadMoreBtn = document.getElementById('load-more-comments');
        if (!loadMoreBtn) return;

        loadMoreBtn.addEventListener('click', () => {
            this.loadMoreComments(loadMoreBtn);
        });
    },

    // Submit comment
    async submitComment(retryData = null) {
        const commentForm = document.getElementById('comment-form');
        const textarea = document.getElementById('comment-text');
        const submitBtn = document.getElementById('comment-submit-btn');

        if (!commentForm || !textarea || !submitBtn) return;

        const text = retryData ? retryData.text : textarea.value.trim();
        const universityId = retryData ? retryData.universityId : commentForm.dataset.universityId;

        if (!text) {
            this.showMessage('error', 'لطفا نظر خود را بنویسید');
            return;
        }

        // Disable button and show loading
        submitBtn.disabled = true;
        const originalText = submitBtn.querySelector('span').textContent;
        submitBtn.querySelector('span').textContent = 'در حال ارسال...';

        try {
            const response = await fetch('/comment/ajax/store', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                },
                body: JSON.stringify({
                    text: text,
                    type: 'App\\Models\\Features\\Universities\\University',
                    commentable_id: universityId,
                })
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle 403 - Profile incomplete
                if (response.status === 403) {
                    // Store comment data for retry
                    this.pendingCommentData = { text, universityId };

                    // Show profile completion modal
                    window.dispatchEvent(new CustomEvent('open-profile-modal', {
                        detail: {
                            callback: () => {
                                // Retry comment submission after profile completion
                                setTimeout(() => {
                                    this.submitComment(this.pendingCommentData);
                                    this.pendingCommentData = null;
                                }, 500);
                            }
                        }
                    }));

                    throw new Error('PROFILE_INCOMPLETE');
                }

                // Handle other errors
                throw new Error(data.message || 'خطا در ارسال نظر');
            }

            if (data.status === 200 && data.success) {
                this.showMessage('success', 'نظر شما با موفقیت ثبت شد.');
                this.insertNewComment(text, universityId);
                textarea.value = '';
            }
        } catch (error) {
            // Don't show error for profile incomplete (modal is shown instead)
            if (error.message === 'PROFILE_INCOMPLETE') {
                return;
            }

            this.showMessage('error', error.message);
        } finally {
            submitBtn.disabled = false;
            submitBtn.querySelector('span').textContent = originalText;
        }
    },

    // Insert new comment into the list
    insertNewComment(text, universityId) {
        const commentsList = document.getElementById('comments-list');
        const noCommentsMsg = document.getElementById('no-comments-message');

        // Hide "no comments" message if exists
        if (noCommentsMsg) {
            noCommentsMsg.remove();
        }

        // Get user info (from auth)
        const userName = '{{ auth()->user()->first_name ?? "" }} {{ auth()->user()->last_name ?? "" }}';
        const userAvatar = '{{ auth()->user()->avatar ?? "" }}';

        // Create new comment HTML
        const newCommentHtml = this.createCommentHTML({
            id: Date.now(), // Temporary ID
            text: text,
            user: { name: userName, avatar: userAvatar },
            created_at: 'الان',
            status: 'publish',
            likes_count: 0,
            dislikes_count: 0,
            replies: []
        });

        // Insert at the beginning of comments list
        if (commentsList) {
            commentsList.insertAdjacentHTML('afterbegin', newCommentHtml);
        }
    },

    // Handle comment like
    async handleCommentLike(likeBtn) {
        const commentId = likeBtn.dataset.commentId;

        likeBtn.disabled = true;
        likeBtn.style.opacity = '0.6';

        try {
            const response = await fetch(`/universities/comments/${commentId}/like`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                }
            });

            const data = await response.json();

            if (data.success) {
                likeBtn.querySelector('.like-count').textContent = data.likes_count;
                const dislikeBtn = likeBtn.closest('.flex').querySelector('.comment-dislike-btn');
                if (dislikeBtn) {
                    dislikeBtn.querySelector('.dislike-count').textContent = data.dislikes_count;
                }
            } else if (data.error) {
                this.showMessage('error', data.message);
            }
        } catch (error) {
            console.error('Error:', error);
            this.showMessage('error', 'خطا در ثبت لایک');
        } finally {
            likeBtn.disabled = false;
            likeBtn.style.opacity = '1';
        }
    },

    // Handle comment dislike
    async handleCommentDislike(dislikeBtn) {
        const commentId = dislikeBtn.dataset.commentId;

        dislikeBtn.disabled = true;
        dislikeBtn.style.opacity = '0.6';

        try {
            const response = await fetch(`/universities/comments/${commentId}/dislike`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                }
            });

            const data = await response.json();

            if (data.success) {
                dislikeBtn.querySelector('.dislike-count').textContent = data.dislikes_count;
                const likeBtn = dislikeBtn.closest('.flex').querySelector('.comment-like-btn');
                if (likeBtn) {
                    likeBtn.querySelector('.like-count').textContent = data.likes_count;
                }
            } else if (data.error) {
                this.showMessage('error', data.message);
            }
        } catch (error) {
            console.error('Error:', error);
            this.showMessage('error', 'خطا در ثبت دیسلایک');
        } finally {
            dislikeBtn.disabled = false;
            dislikeBtn.style.opacity = '1';
        }
    },

    // Handle comment reply
    handleCommentReply(replyBtn) {
        const commentItem = replyBtn.closest('.comment-item');
        const replyFormContainer = commentItem.querySelector('.reply-form-container');

        // Toggle reply form visibility
        replyFormContainer.classList.toggle('hidden');

        // Focus on textarea
        if (!replyFormContainer.classList.contains('hidden')) {
            const textarea = replyFormContainer.querySelector('textarea');
            textarea.focus();
        }
    },

    // Submit reply
    async submitReply(form) {
        const textarea = form.querySelector('textarea');
        const submitBtn = form.querySelector('button[type="submit"]');
        const text = textarea.value.trim();
        const parentId = form.dataset.parentId;
        const commentForm = document.getElementById('comment-form');
        const universityId = commentForm.dataset.universityId;

        if (!text) {
            this.showMessage('error', 'لطفا پاسخ خود را بنویسید');
            return;
        }

        // Disable button and show loading
        submitBtn.disabled = true;
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'در حال ارسال...';

        try {
            const response = await fetch('/comment/ajax/store', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                },
                body: JSON.stringify({
                    text: text,
                    type: 'App\\Models\\Features\\Universities\\University',
                    commentable_id: universityId,
                    reply_to: parentId,
                })
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle 403 - Profile incomplete
                if (response.status === 403) {
                    // Show profile completion modal
                    window.dispatchEvent(new CustomEvent('open-profile-modal', {
                        detail: {
                            callback: () => {
                                // Retry reply submission after profile completion
                                setTimeout(() => this.submitReply(form), 500);
                            }
                        }
                    }));

                    throw new Error('PROFILE_INCOMPLETE');
                }

                // Handle other errors
                throw new Error(data.message || 'خطا در ارسال پاسخ');
            }

            if (data.status === 200 && data.success) {
                this.showMessage('success', 'پاسخ شما با موفقیت ثبت شد و در انتظار تأیید است');
                this.insertNewReply(form, text);
                textarea.value = '';
                form.closest('.reply-form-container').classList.add('hidden');
            }
        } catch (error) {
            // Don't show error for profile incomplete (modal is shown instead)
            if (error.message === 'PROFILE_INCOMPLETE') {
                return;
            }

            this.showMessage('error', error.message);
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    },

    // Insert new reply
    insertNewReply(form, text) {
        // Get user info
        const userName = '{{ auth()->user()->first_name ?? "" }} {{ auth()->user()->last_name ?? "" }}';
        const userAvatar = '{{ auth()->user()->avatar ?? "" }}';

        // Create new reply HTML
        const newReplyHtml = `
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 animate-fade-in">
                <div class="flex items-start gap-2">
                    <div class="flex-shrink-0">
                        ${userAvatar ?
                `<img src="${userAvatar}" alt="${userName}" class="w-8 h-8 rounded-full object-cover">` :
                `<div class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                <i class="fas fa-user text-gray-600 text-xs"></i>
                            </div>`
            }
                    </div>
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                            <h5 class="font-semibold text-gray-900 text-sm">${userName}</h5>
                            <span class="text-xs text-gray-500">الان</span>
                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <i class="fas fa-clock ml-1 text-xs"></i>
                                در انتظار تأیید
                            </span>
                        </div>
                        <p class="text-gray-700 text-sm leading-relaxed">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
                    </div>
                </div>
            </div>
        `;

        // Find or create replies container
        const commentItem = form.closest('.comment-item');
        let repliesContainer = commentItem.querySelector('.comment-item > div > div:last-child > div:last-child');

        if (!repliesContainer || !repliesContainer.classList.contains('mt-4')) {
            // Create new replies container if doesn't exist
            repliesContainer = document.createElement('div');
            repliesContainer.className = 'mt-4 mr-8 space-y-3';
            const contentDiv = form.closest('.flex-1');
            contentDiv.appendChild(repliesContainer);
        }

        // Insert the new reply
        repliesContainer.insertAdjacentHTML('beforeend', newReplyHtml);
    },

    // Handle comment report
    async handleCommentReport(reportBtn) {
        const commentId = reportBtn.dataset.commentId;

        // Simple confirmation dialog
        if (confirm('آیا می‌خواهید این نظر را گزارش دهید؟')) {
            reportBtn.disabled = true;
            reportBtn.style.opacity = '0.6';

            try {
                const response = await fetch(`/universities/comments/${commentId}/report`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                    },
                    body: JSON.stringify({
                        reason_index: 1, // Default reason
                        comments: ''
                    })
                });

                const data = await response.json();

                if (data.success) {
                    this.showMessage('success', data.message);
                    reportBtn.disabled = true;
                    reportBtn.style.opacity = '0.3';
                } else if (data.error) {
                    this.showMessage('error', data.message);
                    reportBtn.disabled = false;
                    reportBtn.style.opacity = '1';
                }
            } catch (error) {
                console.error('Error:', error);
                this.showMessage('error', 'خطا در ثبت گزارش');
                reportBtn.disabled = false;
                reportBtn.style.opacity = '1';
            }
        }
    },

    // Load more comments
    async loadMoreComments(loadMoreBtn) {
        const universityId = loadMoreBtn.dataset.universityId;
        const offset = parseInt(loadMoreBtn.dataset.offset);
        const limit = parseInt(loadMoreBtn.dataset.limit);

        loadMoreBtn.disabled = true;
        loadMoreBtn.innerHTML = '<i class="fas fa-spinner fa-spin ml-2"></i>در حال بارگذاری...';

        try {
            const response = await fetch(`/universities/${universityId}/comments/load-more?offset=${offset}&limit=${limit}`, {
                headers: {
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                }
            });

            const data = await response.json();

            if (data.success && data.comments.length > 0) {
                const commentsList = document.getElementById('comments-list');

                // Append new comments
                data.comments.forEach(comment => {
                    const commentHtml = this.createCommentHTML(comment);
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = commentHtml;
                    commentsList.appendChild(tempDiv.firstElementChild);
                });

                // Update button data
                const newOffset = offset + limit;
                const remainingComments = data.total - newOffset;

                loadMoreBtn.dataset.offset = newOffset;

                if (remainingComments > 0) {
                    loadMoreBtn.innerHTML = `مشاهده نظرات بیشتر (${remainingComments} نظر دیگر)`;
                } else {
                    loadMoreBtn.remove();
                }
            } else {
                loadMoreBtn.remove();
            }
        } catch (error) {
            console.error('Error:', error);
            this.showMessage('error', 'خطا در بارگذاری نظرات');
            loadMoreBtn.innerHTML = 'مشاهده نظرات بیشتر';
        } finally {
            loadMoreBtn.disabled = false;
        }
    },

    // Create comment HTML
    createCommentHTML(comment) {
        const userAvatar = comment.user.avatar
            ? `<img src="${comment.user.avatar}" alt="${comment.user.name}" class="w-10 h-10 rounded-full object-cover">`
            : `<div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center"><i class="fas fa-user text-gray-600"></i></div>`;

        // Create pending badge if comment is not published and user is authenticated
        const isUserLoggedIn = !!document.querySelector('meta[name="csrf-token"]');
        const currentUserId = isUserLoggedIn ? '{{ auth()->id() }}' : null;
        const isOwnComment = currentUserId && comment.author_id == currentUserId;
        const pendingBadge = (comment.status !== 'publish' && isUserLoggedIn && isOwnComment) ?
            `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <i class="fas fa-clock ml-1"></i>
                در انتظار تأیید
            </span>` : '';

        let repliesHTML = '';
        if (comment.replies && comment.replies.length > 0) {
            repliesHTML = '<div class="mt-4 mr-8 space-y-3">';
            comment.replies.forEach(reply => {
                const replyAvatar = reply.user.avatar
                    ? `<img src="${reply.user.avatar}" alt="${reply.user.name}" class="w-8 h-8 rounded-full object-cover">`
                    : `<div class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center"><i class="fas fa-user text-gray-600 text-xs"></i></div>`;

                // Create pending badge for replies
                const replyPendingBadge = (reply.status !== 'publish' && isUserLoggedIn && reply.author_id == currentUserId) ?
                    `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <i class="fas fa-clock ml-1 text-xs"></i>
                        در انتظار تأیید
                    </span>` : '';

                repliesHTML += `
                    <div class="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div class="flex items-start gap-2">
                            <div class="flex-shrink-0">${replyAvatar}</div>
                            <div class="flex-1">
                                <div class="flex items-center gap-2 mb-1">
                                    <h5 class="font-semibold text-gray-900 text-sm">${reply.user.name}</h5>
                                    <span class="text-xs text-gray-500">${reply.created_at}</span>
                                    ${replyPendingBadge}
                                </div>
                                <p class="text-gray-700 text-sm leading-relaxed">${reply.text}</p>
                            </div>
                        </div>
                    </div>
                `;
            });
            repliesHTML += '</div>';
        }

        return `
            <div class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow comment-item" data-comment-id="${comment.id}">
                <div class="flex items-start gap-3">
                    <div class="flex-shrink-0">${userAvatar}</div>
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-2">
                            <h4 class="font-semibold text-gray-900">${comment.user.name}</h4>
                            <span class="text-sm text-gray-500">${comment.created_at}</span>
                            ${pendingBadge}
                        </div>
                        <p class="text-gray-700 leading-relaxed mb-3">${comment.text}</p>
                        <div class="flex items-center gap-4">
                            <button class="comment-like-btn flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors text-sm" data-comment-id="${comment.id}">
                                <i class="fas fa-thumbs-up"></i>
                                <span class="like-count">${comment.likes_count}</span>
                            </button>
                            <button class="comment-dislike-btn flex items-center gap-1 text-gray-500 hover:text-red-600 transition-colors text-sm" data-comment-id="${comment.id}">
                                <i class="fas fa-thumbs-down"></i>
                                <span class="dislike-count">${comment.dislikes_count}</span>
                            </button>
                            <button class="comment-reply-btn flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors text-sm" data-comment-id="${comment.id}">
                                <i class="fas fa-reply"></i>
                                <span>پاسخ</span>
                            </button>
                            <button class="comment-report-btn text-gray-400 hover:text-red-500 transition-colors text-sm" data-comment-id="${comment.id}">
                                <i class="fas fa-flag"></i>
                            </button>
                        </div>
                        <div class="reply-form-container hidden mt-4">
                            <form class="reply-form flex gap-2" data-parent-id="${comment.id}">
                                <textarea name="text" rows="2" class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm" placeholder="پاسخ شما..."></textarea>
                                <button type="submit" class="px-4 py-2 linkedin-bg-blue hover:linkedin-hover-blue text-white rounded-lg transition-colors text-sm">ارسال</button>
                            </form>
                        </div>
                        ${repliesHTML}
                    </div>
                </div>
            </div>
        `;
    }
};
